import React, { useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LoginForm, TLoginFormValues } from 'auth/components/forms/LoginForm';
import {
    TwoFactorAuthForm,
    TTwoFactorFormValues,
} from 'auth/components/forms/TwoFactorAuthForm';
import {
    useRequestAuthCodeMutation,
    useObtainAuthTokenMutation,
} from 'graphql/types';
import { useAuth } from 'auth/hooks';
import { useSnackbarContext } from 'common/context';
import { getIntlPath } from 'common/utils';

import { BaseAuthPage } from './BaseAuthPage';

export const LoginPage: React.FC = () => {
    const { t } = useTranslation();

    const [userId, setUserId] = useState<string | null>(null);
    // Credentials are stored here in case user re-requests auth code
    const [userCredentials, setUserCredentials] =
        useState<TLoginFormValues | null>(null);
    const { login, isLoggedIn } = useAuth();
    const history = useHistory();

    const { setSnackbarState } = useSnackbarContext();

    const [requestAuthCode, { loading: codeRequestLoading }] =
        useRequestAuthCodeMutation({
            update: (_cache, { data }) => {
                if (data && data.requestAuthenticationCode) {
                    const { userId } = data.requestAuthenticationCode;

                    setUserId(userId);
                }
            },
            onError: (error) => {
                setSnackbarState({
                    message: error.message,
                    variant: 'error',
                });
            },
        });
    const [obtainAuthToken, { loading: tokenLoading }] =
        useObtainAuthTokenMutation({
            update: (_cache, { data }) => {
                if (data && data.obtainToken) {
                    const { token, refreshExpiresIn } = data.obtainToken;

                    login(token, refreshExpiresIn);

                    history.push(getIntlPath('invitations'));
                }
            },
            onError: (error) => {
                setSnackbarState({
                    message: error.message,
                    variant: 'error',
                });
            },
        });

    const handleLoginSubmit = (values: TLoginFormValues) => {
        requestAuthCode({
            variables: values,
        });
        setUserCredentials(values);
    };

    const handleCodeSubmit = (values: TTwoFactorFormValues) => {
        if (!userId) return;

        obtainAuthToken({
            variables: {
                userId,
                code: values.code,
            },
        });
    };

    const handleRetryClick = () => {
        if (!userCredentials) return;

        requestAuthCode({
            variables: userCredentials,
        }).then(() => {
            setSnackbarState({
                message: t('messages.verificationResent'),
                variant: 'success',
            });
        });
    };

    if (isLoggedIn()) return <Redirect to={getIntlPath('invitations')} />;

    return (
        <BaseAuthPage>
            {userId ? (
                <TwoFactorAuthForm
                    loading={tokenLoading}
                    onRetryClick={handleRetryClick}
                    onSubmit={handleCodeSubmit}
                />
            ) : (
                <LoginForm
                    loading={codeRequestLoading}
                    onSubmit={handleLoginSubmit}
                />
            )}
        </BaseAuthPage>
    );
};
